<template>
  <form class="physician-search-widget-filters" @submit.prevent="updateFilters">
    <div class="physician-search-widget-filter-item">
      <label class="w-100">
        <div class="physicians-filter-label visually-hidden">{{ nameLabel }}</div>
        <input class="form-control"
               id="physiciansSearchTerm"
               type="search"
               :placeholder="nameLabel"
               v-model="searchTerm">
      </label>
    </div>
    <div class="physician-search-widget-filter-item" v-if="showSpecialtyFilter">
      <label class="w-100">
        <div class="physicians-filter-label visually-hidden">{{ specialtyLabel }}</div>
        <select class="form-select" v-model="specialty" @change="specialtyChanged">
          <option value="">{{ selectLabel }} {{ specialtyLabel }}</option>
          <option v-for="specialty in store.filterOptions.specialties"
                  :key="specialty.codeName"
                  :value="specialty.codeName">
            {{ specialty.title }}
          </option>
        </select>
      </label>
    </div>
    <div class="physician-search-widget-filter-item" v-if="showSubspecialtyFilter">
      <label class="w-100">
        <div class="physicians-filter-label visually-hidden">{{ subspecialtyLabel }}</div>
        <select class="form-select" v-model="subspecialty" :disabled="filteredSubspecialties.length === 0">
          <option value="">{{ selectLabel }} {{ subspecialtyLabel }}</option>
          <option v-for="subspecialty in filteredSubspecialties"
                  :key="subspecialty.codeName"
                  :value="subspecialty.codeName">
            {{ subspecialty.title }}
          </option>
        </select>
      </label>
    </div>
    <div class="physician-search-widget-filter-item" v-if="showServiceLocationFilter">
      <label class="w-100">
        <div class="physicians-filter-label visually-hidden">{{ serviceLocationLabel }}</div>
        <select class="form-select" v-model="serviceLocation" :disabled="filteredServiceLocations.length === 0">
          <option value="">{{ selectLabel }} {{ serviceLocationLabel }}</option>
          <option v-for="serviceLocation in filteredServiceLocations"
                  :key="serviceLocation.codeName"
                  :value="serviceLocation.codeName">
            {{ serviceLocation.title }}
          </option>
        </select>
      </label>
    </div>
    <div class="physician-search-widget-filter-item">
      <div class="form-check">
        <input class="form-check-input" type="checkbox" value="" id="acceptingNewPatients" v-model="acceptingNewPatients">
        <label class="form-check-label" for="acceptingNewPatients">
          {{ acceptingNewPatientsLabel }}
        </label>
      </div>
    </div>
    <div class="physician-search-widget-filter-item">
      <div class="form-check">
        <input class="form-check-input" type="checkbox" value="" id="sameDayAppointments" v-model="sameDayAppointments">
        <label class="form-check-label" for="sameDayAppointments">
          {{ sameDayAppointmentsLabel }}
        </label>
      </div>
    </div>
    <div class="physician-search-widget-filter-item">
      <div class="form-check">
        <input class="form-check-input" type="checkbox" value="" id="acceptsMediCal" v-model="acceptsMediCal">
        <label class="form-check-label" for="acceptsMediCal">
          {{ acceptsMedicalLabel }}
        </label>
      </div>
    </div>
    <div class="physician-search-widget-filter-buttons">
      <div class="d-flex justify-content-start mx-n1">
        <div class="p-1">
          <button type="submit" class="btn btn-primary">
            {{ submitLabel }}
          </button>
        </div>
        <div class="p-1">
          <button type="button" class="btn btn-link text-decoration-underline" @click="clearFilters">
            {{ clearLabel }}
          </button>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { ChpPhysiciansStore } from '../stores/ChpPhysiciansStore'

export default {
  props: {
    showSpecialtyFilter: {
      type: Boolean,
      required: true
    },
    specialtyFilterValue: {
      type: String,
      required: true
    },
    showSubspecialtyFilter: {
      type: Boolean,
      required: true
    },
    subspecialtyFilterValue: {
      type: String,
      required: true
    },
    showServiceLocationFilter: {
      type: Boolean,
      required: true
    },
    serviceLocationFilterValue: {
      type: String,
      required: true
    },
    nameLabel: {
      type: String,
      required: true
    },
    specialtyLabel: {
      type: String,
      required: true
    },
    subspecialtyLabel: {
      type: String,
      required: true
    },
    serviceLocationLabel: {
      type: String,
      required: true
    },
    acceptingNewPatientsLabel: {
      type: String,
      required: true
    },
    sameDayAppointmentsLabel: {
      type: String,
      required: true
    },
    acceptsMedicalLabel: {
      type: String,
      required: true
    },
    selectLabel: {
      type: String,
      required: true
    },
    submitLabel: {
      type: String,
      required: true
    },
    clearLabel: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      valuesInitialized: false,
      searchTerm: '',
      specialty: this.specialtyFilterValue,
      subspecialty: this.subspecialtyFilterValue,
      serviceLocation: this.serviceLocationFilterValue,
      acceptingNewPatients: false,
      sameDayAppointments: false
    }
  },
  computed: {
    filteredSubspecialties() {
      return this.store.filterOptions.subspecialties.filter(s => s.parentCodeName == this.specialty)
    },
    filteredServiceLocations() {
      if (this.specialty) {
        return this.store.filterOptions.serviceLocations.filter(s => s.specialtyCodeNames.includes(this.specialty))
      } else {
        return this.store.filterOptions.serviceLocations
      }
    }
  },
  watch: {
  },
  setup() {
    const store = ChpPhysiciansStore()
    store.disableUrlParameters()
    store.getFilterOptions()
    return {
      store
    }
  },
  created() {
    const urlParameters = new URLSearchParams(window.location.search)
    if (urlParameters.has('q')) {
      this.searchTerm = urlParameters.get('q')
    }
    
    this.updateFilters()
  },
  mounted() {
    this.store.$subscribe((mutation, state) => {
      if (!this.valuesInitialized) {
        this.initializeValues()
      }
    })
  },
  methods: {
    initializeValues() {
      this.valuesInitialized = true
    },
    updateFilters() {
      this.store.updateWidgetFilters(this.searchTerm, this.specialty, this.subspecialty, this.serviceLocation, this.acceptingNewPatients, this.sameDayAppointments, this.acceptsMediCal)
    },
    clearFilters() {
      this.searchTerm = ''
      if (this.showSpecialtyFilter) {
        this.specialty = ''
      }
      if (this.showSubspecialtyFilter) {
        this.subspecialty = ''
      }
      if (this.showServiceLocationFilter) {
        this.serviceLocation = ''
      }
      this.acceptingNewPatients = false
      this.sameDayAppointments = false
      this.acceptsMediCal = false
      this.updateFilters()
    },
    specialtyChanged() {
      this.subspecialty = ''
      this.serviceLocation = ''
    }
  },
  components: {
  }
}
</script>
